import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class UndoRedoService {
  _contexts: {
    [contextKey: string]: {
      history: Undoable[];
      current: Undoable;
      future: Undoable[];
    };
  } = {};

  initialize(contextKey: string, length: number = 25) {
    // Ensure the histories don't exceed the specified length
    const history = [];
    history.push = function () {
      if (this.length >= length) this.shift();
      return Array.prototype.push.apply(this, arguments);
    };

    const context = {
      history,
      current: null,
      future: [],
    };
    this._contexts[contextKey] = context;
    return context;
  }

  next(contextKey: string, undoable: Undoable) {
    // Initialize the context if it doesn't yet exist
    const context = this._contexts[contextKey] || this.initialize(contextKey);

    try {
      // Go forward
      undoable.forward();
    } catch (e) {
      console.warn(`Cannot next; caught ${e}`);
      return;
    }
    undoable.events?.afterFirst?.();

    // Update the context
    if (context.current) context.history.push(context.current);
    context.current = undoable;
    context.future = [];
  }

  undo(contextKey: string) {
    const context = this._contexts[contextKey];
    if (!context.current) return;

    try {
      // Go backward
      context.current.backward();
    } catch (e) {
      console.warn(`Cannot undo; caught ${e}`);
      return;
    }
    context.current.events?.afterUndo?.();

    // Update the context
    context.future.push(context.current);
    context.current = context.history.pop();
    return context.current;
  }

  redo(contextKey: string) {
    const context = this._contexts[contextKey];
    if (!context.future.length) return context.current;

    // Go forward
    const future = context.future.pop();
    try {
      future.forward();
    } catch (e) {
      console.warn(`Cannot redo; caught ${e}`);
      context.future.push(future); // Add the future back
      return;
    }
    future.events?.afterRedo?.();

    // Update the context
    context.history.push(context.current);
    context.current = future;

    return future;
  }

  canUndo(contextKey: string) {
    return !!this._contexts[contextKey]?.current;
  }

  canRedo(contextKey: string) {
    return !!this._contexts[contextKey]?.future.length;
  }

  removeHistory(contextKey: string) {
    delete this._contexts[contextKey];
  }
}

export class Undoable {
  constructor(
    public forward: () => void,
    public backward: () => void,
    public events?: {
      afterFirst?: () => void;
      afterRedo?: () => void;
      afterUndo?: () => void;
    },
  ) {}
}
